import clsx from 'clsx'
import {isValidElement, useCallback, useEffect, useRef, useState} from 'react'
import {Tooltip} from '@yandex-delivery/blocks'
import {Translator} from '@yandex-delivery/core/i18n'

import {AlertIcon, CheckIcon, CrossIcon, InfoIcon} from '../icons'
import {cssTheme} from '../theme'
import {Opacity, TransitionGroup} from '../transition'
import {Typography} from '../typography'
import {classes, setNotificationColor, setNotificationShadow} from './classes'
import {NotificationActionButton} from './NotificationActionButton'
import {startSwipe} from './swipe'
import {CustomNotification, NotificationType} from './types'
import React from 'react'
import {useCopyOnClick} from '@yandex-delivery/hooks'

const TRANSITION_DURATION = 400
const NOTIFICATION_LIFETIME = 10000

const notificationTypeMap: Record<NotificationType, {notificationIcon: JSX.Element; backgroundColor: string}> = {
  error: {
    notificationIcon: <AlertIcon />,
    backgroundColor: cssTheme.default.day.error.hex,
  },
  info: {
    notificationIcon: <InfoIcon />,
    backgroundColor: cssTheme.default.day.backgroundInvert.hex,
  },
  success: {
    notificationIcon: <CheckIcon />,
    backgroundColor: cssTheme.colors.green.normal.hex,
  },
}

type NotificationProps = CustomNotification & {
  remove: VoidFunction
  i18n?: Translator
}
export const Notification = ({
  msg,
  duration = NOTIFICATION_LIFETIME,
  type,
  action,
  remove,
  i18n,
}: NotificationProps) => {
  const [isOn, setIsOn] = useState(true)
  const removalTimeout = useRef<ReturnType<typeof setTimeout>>()
  const {copied, onCopyClick} = useCopyOnClick()
  const {notificationIcon, backgroundColor} = notificationTypeMap[type]
  const shadow = cssTheme.shadows.bottomNormal.hex.replace(/rgba(.*)/, `${backgroundColor}1e`)

  const removeWithTransition = useCallback(() => {
    setIsOn(false)
    if (removalTimeout.current !== undefined) return

    removalTimeout.current = setTimeout(remove, TRANSITION_DURATION)
  }, [remove])

  const handleSwipeStart = (e: React.PointerEvent<HTMLElement>) => {
    // Мышкой свайпаем только вправо
    const allowedDirection = e.pointerType === 'mouse' ? 'right' : 'both'
    startSwipe(allowedDirection, remove, e)
  }

  useEffect(() => {
    const timeout = setTimeout(removeWithTransition, duration)

    return () => {
      clearTimeout(timeout)
      clearTimeout(removalTimeout.current)
      removalTimeout.current = undefined
    }
    // надо это запустить только один раз, иначе длительность уведомления будет обновляться
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <TransitionGroup on={isOn}>
      <Opacity duration={TRANSITION_DURATION}>
        <li>
          <div
            className={classes.notification}
            onPointerDown={handleSwipeStart}
            style={{...setNotificationColor(backgroundColor), ...setNotificationShadow(shadow)}}
          >
            <div className={classes.notificationIcon}>{notificationIcon}</div>
            {isValidElement(msg) ? (
              msg
            ) : (
              <Tooltip
                className={classes.toolTip}
                opened={copied}
                placement="top"
                title={i18n ? i18n('block.notifications.notification-error.copied', 'Скопировано') : 'Скопировано'}
              >
                <Typography
                  onClick={() => onCopyClick(String(msg))}
                  className={classes.notificationText}
                  color="invert"
                  group="tight"
                  variant="body2"
                >
                  {msg}
                </Typography>
              </Tooltip>
            )}
            {!!action && <NotificationActionButton remove={remove} {...action} />}
            <button
              className={clsx(classes.closeNotificationBtn, classes.expandedClickArea)}
              data-e2e="notification-button-close"
              onPointerUp={removeWithTransition}
            >
              <CrossIcon />
            </button>
          </div>
        </li>
      </Opacity>
    </TransitionGroup>
  )
}
